import { Html5Qrcode } from "html5-qrcode";

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Icon,
  Snackbar,
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";

function QRScannerField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [isScanning, setIsScanning] = useState(false);
  const [scannedText, setScannedText] = useState(null);
  const [scannerObj, setsCannerObj] = useState(null);

  const config = { fps: 10, qrbox: { width: 250, height: 250 } };

  function onScanSuccess(decodedText, decodedResult) {
    setScannedText(decodedText);
    closeScanning();
  }

  function openScanner(camera) {
    scannerObj.start(camera, config, onScanSuccess);
    setIsScanning(true);
  }

  function closeScanning() {
    if (scannerObj === null) return;

    try {
      scannerObj.stop();
      setIsScanning(false);
    } catch {}
  }

  function initScanning() {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          var cameraId = devices[0].id;
          let camera = { deviceId: { exact: cameraId } };
          if (devices.length > 1) {
            camera = { facingMode: "environment" };
          }
          openScanner(camera);
        }
      })
      .catch((err) => {
        setErrorMsg(`Code scan error = ${err}`);
        showFieldError(true);
      });
  }

  useEffect(() => {
    setsCannerObj(new Html5Qrcode(`${fieldRef}_scanner`));
  }, []);

  return (
    <Box>
      {/* ERROR */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Box sx={{ minWidth: 120, width: "100%" }}>
        {/* HEADER */}
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography
              variant="h6"
              fontWeight="light"
              fontSize="small"
              mb={2}
            >
              {displayName}
            </Typography>
          </Grid>

          <Grid mt={-1} item xs={6}>
            <Box align="right">
              {!isScanning ? (
                <IconButton onClick={initScanning}>
                  <Icon>qr_code_scanner</Icon>
                </IconButton>
              ) : (
                <IconButton onClick={closeScanning}>
                  <Icon color="error">close</Icon>
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ✅ CAPTURE BOX (UI ONLY) */}
        <Box
          sx={{
            border: "2px dashed #d2d6da",
            borderRadius: "12px",
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#f8f9fa",
            mt: 2,
          }}
        >
          {/* Idle State */}
          {!isScanning && !scannedText && (
            <Box textAlign="center">
              <IconButton
                color="primary"
                onClick={initScanning}
                sx={{
                  width: 64,
                  height: 64,
                  backgroundColor: "#e3f2fd",
                }}
              >
                <Icon fontSize="large">qr_code_scanner</Icon>
              </IconButton>

              <Typography
                variant="caption"
                mt={1}
                display="block"
              >
                Tap to scan QR code
              </Typography>
            </Box>
          )}

          {/* Scanner (UNCHANGED, just styled container) */}
          <Box
            id={`${fieldRef}_scanner`}
            sx={{
              width: isScanning ? "100%" : 0,
              height: isScanning ? "100%" : 0,
            }}
          />

          {/* Stop button overlay */}
          {isScanning && (
            <IconButton
              onClick={closeScanning}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.2)",
                boxShadow: 3,
              }}
            >
              <Icon color="error">close</Icon>
            </IconButton>
          )}

          {/* Result State */}
          {!isScanning && scannedText && (
            <Box textAlign="center" px={2}>
              <Typography variant="caption" display="block" mb={1}>
                Scan Result
              </Typography>

              <Typography
                variant="caption"
                sx={{ wordBreak: "break-all" }}
              >
                {scannedText}
              </Typography>

              <Box mt={1}>
                <Button
                  size="small"
                  onClick={() => setScannedText(null)}
                >
                  clear
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* ORIGINAL RESULT TEXT (kept intact) */}
        {/* <Box mt={isScanning ? 3 : 0}>
          {!scannedText && (
            <Typography variant="h6" fontSize={14}>
              <small>Scan a QR code</small>
            </Typography>
          )}
        </Box> */}
      </Box>

      <input
        id={`dff_${fieldRef}`}
        type="hidden"
        name={fieldRef}
        value={scannedText}
      />
    </Box>
  );
}

QRScannerField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

QRScannerField.defaultProps = {
  options: null,
};

export default QRScannerField;